import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import Image from 'next/image'
import {useLinkResolver} from 'prismicio'
import {ButtonDocument, CtaV2SliceFullWidthImage} from 'prismicio-types'
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import {Source} from 'src/common/SourceSet'

type FullWidthVariationProps = {
  slice: CtaV2SliceFullWidthImage
  pageUid: string
  componentIndex: number
  buttonsData?: ButtonDocument[]
}

export const FullWidthVariation = ({slice, buttonsData, pageUid, componentIndex}: FullWidthVariationProps) => {
  const {linkResolver} = useLinkResolver()

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <Container data-slice-variation={slice.variation} className="px-5 max-lg:py-10 md:px-10 lg:px-5 lg:pb-20 xl:px-0">
      <div className="relative isolate mx-auto flex h-[553px] w-full max-w-[1200px] flex-col justify-between overflow-hidden rounded-lg p-6 md:p-10">
        {isFilled.image(slice.primary.image) && (
          <picture className="absolute inset-0 h-full w-full">
            {isFilled.image(slice.primary.mobile_image) && (
              <Source src={slice.primary.mobile_image.url || ''} media="(max-width: 768px)" />
            )}
            <Image
              src={slice.primary.image.url!}
              alt=""
              fill
              quality={100}
              unoptimized
              priority
              className="h-full object-cover"
            />
          </picture>
        )}
        {isFilled.group(slice.primary.ratings) && (
          <RatingsSection ratings={slice.primary.ratings} type="Animated" centered={false} version="v2" />
        )}
        <div className="z-10 flex w-full flex-col gap-8">
          <div>
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({children}) => (
                  <h2 className="md:text-tg-h1 text-[32px] leading-[normal] text-moss-white">{children}</h2>
                ),
              }}
            />
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:gap-4">
            {primaryButtonData ? (
              <ButtonV2
                variant="primary"
                href={linkResolver(primaryButtonData.link)}
                trackingData={{
                  mktgId: primaryButtonData.marketing_id || '',
                  dataType: primaryButtonData.intent || '',
                  location: `component-${componentIndex}-cta-v3`,
                  pageName: pageUid,
                }}
                className="w-fit text-[12px] font-semibold uppercase"
                size="medium"
                id={`component-${componentIndex}-cta-1`}
                border={false}
              >
                {primaryButtonData.label}
              </ButtonV2>
            ) : null}
            {secondaryButtonData ? (
              <ButtonV2
                variant="secondary"
                href={linkResolver(secondaryButtonData.link)}
                trackingData={{
                  mktgId: secondaryButtonData.marketing_id || '',
                  dataType: secondaryButtonData.intent || '',
                  location: `component-${componentIndex}-cta-v3`,
                  pageName: pageUid,
                }}
                className="w-fit text-[12px] font-semibold uppercase"
                size="medium"
                id={`component-${componentIndex}-cta-2`}
                border={false}
              >
                {secondaryButtonData.label}
              </ButtonV2>
            ) : null}
          </div>
        </div>
      </div>
    </Container>
  )
}
